import React, { useState, useEffect } from 'react';
import {
  Calendar,
  MapPin,
  Mail,
  ExternalLink,
  Snowflake,
  Camera,
  Menu,
  X,
  Sun,
  Moon,
  Utensils,
  Cloud,
  Phone,
  Navigation,
  Mountain,
  Bike,
  Music,
  Compass,
  BedDouble,
  Ticket,
  Palette,
} from 'lucide-react';
import { season } from '../config/season';
import { site, allSites } from '../config/sites';
import Weather from './Weather';
import Webcams from './Webcams';
import {
  eventCalendars,
  festivals,
  visitorsGuide,
  skiingLinks,
  bikingLinks,
  offroadingLinks,
  trails,
  cultureLinks,
  diningLinks,
  lodgingLinks,
  weatherLinks,
  gettingAroundLinks,
  visitorResources,
  phoneNumbers,
  map,
  apps,
  contactEmail,
} from '../data/links';

// ---------- analytics ----------
const trackEvent = (action, category, label, value) => {
  if (window.gtag) {
    window.gtag('event', action, { event_category: category, event_label: label, value });
  }
};
const handleSectionClick = (name) => trackEvent('section_navigate', 'Navigation', name);
const handleExternalLink = (name, url) => trackEvent('external_link_click', 'External Links', name, url);

// ---------- navigation ----------
const NAV_LINKS = [
  { label: 'Events', href: '#events-section' },
  { label: 'Things to Do', href: '#things-to-do-section' },
  { label: 'Stay & Eat', href: '#dining-section' },
  { label: 'Getting Here', href: '#getting-around-section' },
  { label: 'Conditions', href: '#weather-section' },
  { label: 'Contact', href: '#contact-section' },
];

const BASE_ORDER = ['events', 'guide', 'things', 'stay', 'getting', 'conditions', 'contact'];
const sectionOrder =
  site.lead === 'events' ? BASE_ORDER : [site.lead, ...BASE_ORDER.filter((k) => k !== site.lead)];

// Each section owns one accent; everything else stays neutral.
const SECTION_ACCENT = {
  events: 'amber',
  guide: site.accent,
  things: 'emerald',
  stay: 'rose',
  getting: 'indigo',
  conditions: 'sky',
  contact: 'slate',
};

// ---------- small shared pieces ----------
const Wordmark = ({ className = '' }) => (
  <span className={`inline-flex items-center gap-2 ${className}`}>
    <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[var(--accent)] text-white">
      <Mountain className="h-5 w-5" strokeWidth={2.25} />
    </span>
    <span className="font-bold tracking-tight">{site.name}</span>
  </span>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-6">
    <div className="p-3 bg-[var(--accent-soft)] rounded-lg flex-shrink-0">
      <Icon className="h-6 w-6 text-[var(--accent)]" />
    </div>
    <div>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white leading-tight">{title}</h2>
      {subtitle && <p className="mt-1 text-gray-600 dark:text-gray-400">{subtitle}</p>}
    </div>
  </div>
);

const SubHeader = ({ icon: Icon, title, note }) => (
  <div className="flex items-baseline justify-between gap-4 mb-3 mt-2">
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white inline-flex items-center gap-2">
      {Icon && <Icon className="h-5 w-5 text-[var(--accent)]" />}
      {title}
    </h3>
    {note && <span className="text-xs text-gray-500 dark:text-gray-400 text-right">{note}</span>}
  </div>
);

const Section = ({ id, accent, children, className = '' }) => (
  <section
    id={id}
    className={`accent-${accent} bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8 mb-8 border-t-4 border-[var(--accent)] ${className}`}
  >
    {children}
  </section>
);

const LinkCard = ({ link }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => handleExternalLink(link.name, link.url)}
    className="group flex items-start justify-between gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-[var(--accent)] hover:shadow-md transition-all duration-200"
  >
    <div className="min-w-0">
      <span className="block font-medium text-[var(--accent)] group-hover:text-[var(--accent-strong)] transition-colors">
        {link.name}
      </span>
      {link.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{link.description}</p>}
    </div>
    <ExternalLink className="h-4 w-4 mt-1 text-gray-400 dark:text-gray-500 group-hover:text-[var(--accent)] flex-shrink-0" />
  </a>
);

const LinkGrid = ({ links, cols = 2 }) => (
  <div className={`grid grid-cols-1 ${cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3`}>
    {links.map((l) => (
      <LinkCard key={l.url} link={l} />
    ))}
  </div>
);

const ChipGrid = ({ links }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    {links.map((l) => (
      <a
        key={l.url}
        href={l.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleExternalLink(l.name, l.url)}
        className="group flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 hover:border-[var(--accent)] hover:bg-[var(--accent-soft)] transition-all"
      >
        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[var(--accent-strong)]">
          {l.name}
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-gray-400 group-hover:text-[var(--accent)] flex-shrink-0" />
      </a>
    ))}
  </div>
);

const InlineLinks = ({ links }) => (
  <p className="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
    {links.map((l) => (
      <a
        key={l.url}
        href={l.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleExternalLink(l.name, l.url)}
        className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
      >
        {l.name}
        <ExternalLink className="h-3 w-3" />
      </a>
    ))}
  </p>
);

// ---------- page ----------
const TellurideEvents = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return saved === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Per-domain document metadata
  useEffect(() => {
    document.title = site.name;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', site.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && site.key !== 'events') {
      canonical.setAttribute('href', `${window.location.origin}/`);
    }
  }, []);

  const heroPrefix = site.heroPrefix || (season === 'summer' ? 'tride-summer' : 'tride');
  // Keep in sync with the hero preload script in public/index.html.
  const heroSrcSet = (ext) =>
    [768, 1280, 1920, 3840].map((w) => `/images/${heroPrefix}${w}.${ext} ${w}w`).join(', ');

  const NavBar = () => (
    <nav className="sticky top-0 z-50 bg-white/85 dark:bg-gray-900/85 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a href="/" className="text-gray-900 dark:text-white">
          <Wordmark className="text-lg" />
        </a>

        <div className="hidden md:flex items-center space-x-6">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => handleSectionClick(l.label)}
              className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[var(--accent)] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => setDarkMode((d) => !d)}
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600" />}
          </button>
        </div>

        <div className="flex md:hidden items-center space-x-1">
          <button
            onClick={() => setDarkMode((d) => !d)}
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600 dark:text-gray-300" />}
          </button>
          <button
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-2 space-y-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => {
                handleSectionClick(l.label);
                setMobileMenuOpen(false);
              }}
              className="block py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[var(--accent)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );

  const Hero = () => (
    <div className="relative h-[520px] md:h-[600px]">
      <picture className="absolute inset-0">
        <source type="image/avif" srcSet={heroSrcSet('avif')} sizes="100vw" />
        <img
          src={`/images/${heroPrefix}768.webp`}
          srcSet={heroSrcSet('webp')}
          sizes="100vw"
          alt="Telluride, Colorado"
          className="w-full h-full object-cover"
          width="1920"
          height="600"
          fetchpriority="high"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/50" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center h-full text-center">
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white animate-fade-in drop-shadow-lg">
          {site.title}
        </h1>
        <p className="mt-6 text-lg md:text-2xl text-gray-100 max-w-3xl font-light drop-shadow">{site.tagline}</p>
        <a
          href={site.cta.href}
          onClick={() => handleSectionClick(site.cta.label)}
          className="mt-8 px-8 py-3 bg-[var(--accent)] hover:bg-[var(--accent-strong)] text-white rounded-full transition-all transform hover:scale-105 duration-200 font-medium shadow-lg"
        >
          {site.cta.label}
        </a>
      </div>
    </div>
  );

  // ----- sections -----
  const EventsSection = () => {
    const festivalsFirst = site.featured === 'festivals';
    const calendars = (
      <div key="calendars">
        <SubHeader icon={Calendar} title="Event Calendars" />
        <LinkGrid links={eventCalendars} />
      </div>
    );
    const fests = (
      <div key="festivals">
        <SubHeader icon={Music} title="Marquee Festivals" note="Dates change yearly. Each site has the current lineup." />
        <LinkGrid links={festivals} cols={3} />
      </div>
    );
    return (
      <Section id="events-section" accent={SECTION_ACCENT.events}>
        <SectionHeader icon={Ticket} title="Events & Festivals" subtitle="What's on in Telluride and Mountain Village" />
        <div className="space-y-8">{festivalsFirst ? [fests, calendars] : [calendars, fests]}</div>
      </Section>
    );
  };

  const GuideSection = () => (
    <Section id="guide-section" accent={SECTION_ACCENT.guide}>
      <a
        href={visitorsGuide.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleExternalLink(visitorsGuide.name, visitorsGuide.url)}
        className="group flex flex-col md:flex-row items-center gap-6 md:gap-10"
      >
        <div className="w-40 md:w-48 flex-shrink-0">
          <img
            src={visitorsGuide.image}
            alt={`${visitorsGuide.name} cover`}
            className="rounded-lg shadow-md transform group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            width="400"
            height="483"
          />
        </div>
        <div className="text-center md:text-left">
          <p className="text-sm uppercase tracking-wide text-[var(--accent)] font-medium">{visitorsGuide.name}</p>
          <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white group-hover:text-[var(--accent)] transition-colors">
            {visitorsGuide.title}
          </h2>
          <p className="mt-3 text-gray-600 dark:text-gray-400">{visitorsGuide.description}</p>
          <span className="mt-4 inline-flex items-center text-[var(--accent)] text-sm font-medium">
            View Guide <ExternalLink className="ml-2 h-4 w-4" />
          </span>
        </div>
      </a>
    </Section>
  );

  const ThingsSection = () => {
    const isSummer = season === 'summer';
    const featured = site.featured === 'biking' || site.featured === 'offroading' ? site.featured : null;

    const biking = (
      <div key="biking">
        <SubHeader
          icon={Bike}
          title="Hiking & Biking"
          note={!isSummer ? 'Bike Park and high trails are seasonal. Check status before you go.' : null}
        />
        <LinkGrid links={bikingLinks} cols={3} />
      </div>
    );

    const offroading = (
      <div key="offroading">
        <SubHeader
          icon={Compass}
          title="Offroading & Passes"
          note={!isSummer ? 'High passes close with snow, typically fall through early summer.' : null}
        />
        <LinkGrid links={offroadingLinks} />
        <h4 className="mt-6 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Popular Trails
        </h4>
        <ChipGrid links={trails} />
      </div>
    );

    const skiing = (
      <div key="skiing">
        <SubHeader icon={Snowflake} title="Skiing & Winter" />
        <LinkGrid links={skiingLinks} cols={3} />
      </div>
    );

    const skiTeaser = (
      <div
        key="ski-teaser"
        className="accent-sky rounded-xl bg-[var(--accent-soft)] p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6"
      >
        <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">
          <Snowflake className="h-5 w-5 text-[var(--accent)]" /> Planning a ski trip?
        </span>
        <InlineLinks links={skiingLinks.slice(0, 3)} />
      </div>
    );

    const culture = (
      <div key="culture">
        <SubHeader icon={Palette} title="Arts & Culture" note="Year-round" />
        <LinkGrid links={cultureLinks} cols={3} />
      </div>
    );

    let blocks;
    if (isSummer) {
      blocks = [biking, offroading, culture, skiTeaser];
    } else {
      blocks = [skiing, culture];
      if (featured) blocks.splice(1, 0, featured === 'biking' ? biking : offroading);
    }
    if (featured) {
      const lead = blocks.find((b) => b.key === featured);
      if (lead) blocks = [lead, ...blocks.filter((b) => b.key !== featured)];
    }

    return (
      <Section id="things-to-do-section" accent={SECTION_ACCENT.things}>
        <SectionHeader
          icon={MapPin}
          title="Things to Do"
          subtitle={isSummer ? 'Summer and fall in the San Juans' : 'Winter in the San Juans'}
        />
        <div className="space-y-8">{blocks}</div>
      </Section>
    );
  };

  const StaySection = () => (
    <Section id="dining-section" accent={SECTION_ACCENT.stay}>
      <SectionHeader icon={Utensils} title="Stay & Eat" subtitle="A few favorites, plus the full local guides" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <SubHeader icon={Utensils} title="Restaurants" />
          <div className="space-y-3">
            {diningLinks.map((l) => (
              <LinkCard key={l.url} link={l} />
            ))}
          </div>
        </div>
        <div>
          <SubHeader icon={BedDouble} title="Lodging" />
          <div className="space-y-3">
            {lodgingLinks.map((l) => (
              <LinkCard key={l.url} link={l} />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );

  const GettingSection = () => (
    <Section id="getting-around-section" accent={SECTION_ACCENT.getting}>
      <SectionHeader icon={Navigation} title="Getting Here & Around" subtitle="Flights, drives, the free gondola, and town transit" />
      <LinkGrid links={gettingAroundLinks} />
      <div
        id="location-section"
        className="mt-6 relative group cursor-pointer rounded-xl overflow-hidden"
        onClick={() => window.open(map.url, '_blank')}
      >
        <iframe
          title="Map of Telluride, Colorado"
          className="w-full h-72 md:h-96"
          src={map.embed}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200" />
        <div className="absolute top-4 right-4 bg-white dark:bg-gray-700 rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <ExternalLink className="h-5 w-5 text-[var(--accent)]" />
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">Click the map to open in Google Maps</p>
    </Section>
  );

  const ConditionsSection = () => (
    <Section id="weather-section" accent={SECTION_ACCENT.conditions}>
      <SectionHeader icon={Cloud} title="Weather & Conditions" subtitle="Live forecast, mountain cam, and snow reports" />
      <Weather />
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div id="webcam-section" className="lg:col-span-2">
          <SubHeader icon={Camera} title="Live Mountain Cams" />
          <Webcams onExternalLink={handleExternalLink} />
        </div>
        <div>
          <SubHeader title="More Conditions" />
          <div className="space-y-3">
            {weatherLinks.map((l) => (
              <LinkCard key={l.url} link={l} />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );

  const ContactSection = () => (
    <Section id="contact-section" accent={SECTION_ACCENT.contact}>
      <SectionHeader icon={Mail} title="Contact & Resources" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <SubHeader title="Get in Touch" />
          <a
            href={`mailto:${contactEmail}`}
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-[var(--accent-strong)] transition-colors"
          >
            <Mail className="h-5 w-5 text-[var(--accent)] mr-2" />
            {contactEmail}
          </a>
          <div className="mt-6">
            <SubHeader title="Visitor Resources" />
            <div className="space-y-2">
              {visitorResources.map((r) => (
                <a
                  key={r.url}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleExternalLink(r.name, r.url)}
                  className="flex items-center text-gray-600 dark:text-gray-400 hover:text-[var(--accent-strong)] transition-colors"
                >
                  <ExternalLink className="h-4 w-4 mr-2 flex-shrink-0" />
                  {r.name}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div>
          <SubHeader title="Important Phone Numbers" />
          <div className="space-y-3">
            {phoneNumbers.map((p) => (
              <div key={p.label} className="flex items-center">
                <Phone className={`h-4 w-4 mr-2 flex-shrink-0 ${p.urgent ? 'text-red-500' : 'text-[var(--accent)]'}`} />
                <span className="text-gray-600 dark:text-gray-400">
                  <strong className="text-gray-900 dark:text-white">{p.label}:</strong>{' '}
                  <a href={`tel:${p.tel}`} className="hover:text-[var(--accent-strong)] transition-colors">
                    {p.value}
                  </a>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );

  const Footer = () => {
    const sisters = Object.values(allSites).filter((s) => s.key !== site.key);
    return (
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <Wordmark className="text-xl" />
              <p className="mt-4 text-gray-400 text-sm">{site.description}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-4">On this page</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {NAV_LINKS.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} onClick={() => handleSectionClick(l.label)} className="hover:text-white transition-colors">
                      {l.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="/agents/index.md"
                    onClick={() => handleExternalLink('Agent Markdown', 'https://tellurideevents.com/agents/index.md')}
                    className="hover:text-white transition-colors"
                  >
                    Agent Markdown
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-4">Our other sites</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {sisters.map((s) => (
                  <li key={s.key}>
                    <a
                      href={`https://${s.hosts[0]}/`}
                      onClick={() => handleExternalLink(s.name, `https://${s.hosts[0]}/`)}
                      className="hover:text-white transition-colors"
                    >
                      {s.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-300 mb-4">Telluride Mobile App</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                {apps.map((a) => (
                  <li key={a.url}>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 hover:text-white transition-colors"
                    >
                      <img src="/images/pick.webp" alt="" className="w-5 h-5" loading="lazy" />
                      <span>{a.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Telluride Events. All rights reserved.
          </div>
        </div>
      </footer>
    );
  };

  const sections = {
    events: <EventsSection key="events" />,
    guide: <GuideSection key="guide" />,
    things: <ThingsSection key="things" />,
    stay: <StaySection key="stay" />,
    getting: <GettingSection key="getting" />,
    conditions: <ConditionsSection key="conditions" />,
    contact: <ContactSection key="contact" />,
  };

  return (
    <div className={`accent-${site.accent} min-h-screen bg-gray-100 dark:bg-gray-900`}>
      <NavBar />
      <Hero />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative pb-8">
        {sectionOrder.map((k) => sections[k])}
      </main>
      <Footer />
    </div>
  );
};

export default TellurideEvents;
